import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { InvoiceModel, PaymentOrigin } from '@frontend/api-invoices';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { PaymentsFeatureFlags, useMemoizedArray } from '@frontend/payments-hooks';
import { useAppFlagStore } from '@frontend/shared';
import {
  UseCreatePaymentIntentSyncOptions,
  useInvoicePerson,
  useConfirmStripePaymentViaElements,
  useMakeStripePayment,
} from '../../../../../hooks';

export interface UseStripeManualCardEntryPaymentOptions {
  cardName?: string;
  receiptEmail: string;
  invoice?: InvoiceModel;
  onInvoiceAlreadyPaid: UseCreatePaymentIntentSyncOptions['onInvoiceAlreadyPaid'];
  onRefetchInvoice: UseCreatePaymentIntentSyncOptions['onRefetchInvoice'];

  /**
   * TODO: Remove once disabled fallback is removed
   */
  paymentMethod?: PaymentMethod;
}

export const useStripeManualCardEntryPayment = ({
  cardName,
  receiptEmail,
  invoice,
  paymentMethod,
  onInvoiceAlreadyPaid,
  onRefetchInvoice,
}: UseStripeManualCardEntryPaymentOptions) => {
  const locationId = invoice?.locationId;
  const personId = invoice?.person.id;
  const { personEmail: email } = useInvoicePerson(invoice);
  const amount = invoice?.billedAmount ?? 0;

  const { makePayment: makePaymentEnabled } = useConfirmStripePaymentViaElements({
    amount,
    email,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    setupFutureUsage: null,
    invoiceId: invoice?.id,
    receiptEmail,
    cardholderName: cardName,
    onInvoiceAlreadyPaid,
    onRefetchInvoice,
  });

  /**
   * TODO: Temp fallback code
   * Remove once FF PaymentsFeatureFlags.EnableSurcharging is rolled out
   */

  const { getFeatureFlagValue } = useAppFlagStore();

  const locationIds = useMemoizedArray([locationId]);

  const enableSurcharging = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableSurcharging,
    locationIds: locationIds,
    strategy: 'ALL',
  });

  const { makePayment: makePaymentDisabled } = useMakeStripePayment({
    amount,
    email,
    personId,
    locationId,
    origin: PaymentOrigin.ManualCardEntry,
    setupFutureUsage: null,
    paymentMethod,
    includeElementsInfo: true,
    invoiceId: invoice?.id,
    receiptEmail,
    cardholderName: cardName,
    onInvoiceAlreadyPaid,
    onRefetchInvoice,
  });

  const makePayment = useMemo(() => {
    if (enableSurcharging) {
      return makePaymentEnabled;
    }
    return makePaymentDisabled;
  }, [enableSurcharging, makePaymentEnabled, makePaymentDisabled]);

  const { mutateAsync: makeManualCardEntryPayment, isLoading: makingManualCardEntryPayment } = useMutation({
    mutationFn: async () => makePayment(),
  });
  return { makeManualCardEntryPayment, makingManualCardEntryPayment };
};
